<template>
  <v-container class="pa-0">
    <v-container>
      <v-layout row wrap>
        <v-flex md3>
          <v-card>
            <v-card-title>
              <span class="title font-weight-light">{{ $t("reports.filter") }}</span>
            </v-card-title>
            <v-card-text>
              <v-form ref="form">
                <v-flex md12>
                  <v-autocomplete
                    name="course.course_id"
                    v-model="search_params.course_id"
                    :items="courses"
                    item-text="name"
                    item-value="id"
                    :label="$t('courses.title')"
                    autocomplete="new-password"
                    clearable
                  ></v-autocomplete>
                </v-flex>
                <v-flex md12>
                  <v-autocomplete
                    name="course.coursetype_ids"
                    v-model="search_params.coursetype_ids"
                    :items="coursetypes"
                    item-text="fullname"
                    item-value="id"
                    :label="$t('coursetypes.title')"
                    autocomplete="new-password"
                    multiple
                    clearable
                  >
                    <template v-slot:selection="{ item, index }">
                      <span v-if="index === 0">{{ item.fullname }}</span>
                      <span v-if="index === 1" class="grey--text caption"> (+{{ search_params.coursetype_ids.length - 1 }} others) </span>
                    </template>
                  </v-autocomplete>
                </v-flex>
                <v-flex md12>
                  <v-dialog ref="dialog" v-model="from_date_menu" persistent width="290px">
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="computedFromDate"
                        :label="$t('reports.from_date')"
                        append-icon="event"
                        @click:append="from_date_menu = true"
                        :hint="'Format: ' + DATE_FORMAT"
                        autocomplete="new-password"
                      ></v-text-field>
                    </template>
                    <v-date-picker color="primary" v-model="search_params.start_date" first-day-of-week="1" :locale="this.$locale_bcp47()">
                      <v-spacer></v-spacer>
                      <v-btn text color="primary" @click="from_date_menu = false" class="darken-2">{{ $t("global.ok") }}</v-btn>
                    </v-date-picker>
                  </v-dialog>
                </v-flex>
                <v-flex md12>
                  <v-dialog ref="dialog" v-model="to_date_menu" persistent width="290px">
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="computedToDate"
                        :label="$t('reports.to_date')"
                        append-icon="event"
                        @click:append="to_date_menu = true"
                        :hint="'Format: ' + DATE_FORMAT"
                        autocomplete="new-password"
                      ></v-text-field>
                    </template>
                    <v-date-picker color="primary" v-model="search_params.end_date" first-day-of-week="1" :locale="this.$locale_bcp47()">
                      <v-spacer></v-spacer>
                      <v-btn text color="primary" @click="to_date_menu = false" class="darken-2">{{ $t("global.ok") }}</v-btn>
                    </v-date-picker>
                  </v-dialog>
                </v-flex>
              </v-form>
              <v-flex xs12 class="mt-3">
                <action-button type="run_report" class="ml-0" small @click="filter" />
                <action-button type="reset" @click="reset" small color="error" />
                <action-button type="default" :text="$t('reports.export_to_excel')" icon="get_app" small @click="exportExcel()" />
              </v-flex>
            </v-card-text>
          </v-card>
        </v-flex>

        <v-flex md9 class="pl-3">
          <v-data-table
            :headers="headers"
            :items="data"
            :items-per-page="data.length"
            :options.sync="pagination"
            :footer-props="{
              showFirstLastPage: true,
              'items-per-page-options': this.DATATABLE_ITEM_PER_PAGE_OPTIONS,
              'items-per-page-all-text': $t('global.items_per_page_all_text'),
              'items-per-page-text': $t('global.items_per_page_text'),
              'page-text': $t('global.page_text')
            }"
            :server-items-length="total"
            :loading="show_loading"
            :loading-text="$t('global.loading_text')"
            :no-data-text="$t('global.no_data_text')"
            :no-results-text="$t('global.no_results_text')"
            :must-sort="true"
          >
            <template v-slot:item.created_at="{ item }">
              {{ moment.utc(item.course.created_at).format(DATE_FORMAT) }}
            </template>
            <template v-slot:item.start_date="{ item }">
              {{ moment.utc(item.course.start_date).format(DATE_FORMAT) }}
            </template>
            <template v-slot:item.end_date="{ item }">
              {{ moment.utc(item.course.end_date).format(DATE_FORMAT) }}
            </template>
            <template v-slot:item.attended="{ item }">
              {{ item.attended ? $t("global.yes") : $t("global.no") }}
            </template>
          </v-data-table>
        </v-flex>
      </v-layout>
    </v-container>
  </v-container>
</template>

<script>
export default {
  name: "reportStudentCourseOverview",
  data() {
    return {
      courses: [],
      coursetypes: [],
      search_params: {},
      from_date_menu: false,
      to_date_menu: false,

      url: "",
      data: [],
      total: 0,
      show_loading: false,
      show_filter: false,
      refresh: false,
      pagination: {
        itemsPerPage: this.$getSetting("rows_per_page"),
        sortBy: ["id"],
        page: 1
      }
    }
  },
  created() {
    this.$set(this.search_params, "start_date", this.moment().clone().startOf("month").format(this.MYSQL_DATE_FORMAT))
    this.$set(this.search_params, "end_date", this.moment().clone().endOf("month").format(this.MYSQL_DATE_FORMAT))
    this.ddlist()
  },
  computed: {
    headers: {
      get() {
        return [
          { text: this.$t("reports.first_name"), value: "first_name" },
          { text: this.$t("reports.last_name"), value: "last_name" },
          { text: this.$t("reports.company"), value: "organisation.name" },
          { text: this.$t("reports.course_name"), value: "course.name" },
          { text: this.$t("reports.course_type"), value: "course.course_type" },
          { text: this.$t("reports.address"), value: "course.city" },
          { text: this.$t("reports.week"), value: "course.week" },
          { text: this.$t("reports.enrolled_on"), value: "created_at" },
          { text: this.$t("reports.start_date"), value: "start_date" },
          { text: this.$t("reports.end_date"), value: "end_date" },
          { text: this.$t("reports.attended"), value: "attended" }
        ]
      }
    },
    computedFromDate: {
      get() {
        return this.$dateFormatGetter(this.search_params.start_date)
      },
      set(val) {
        this.$set(this.search_params, "start_date", this.$dateFormatSetter(val))
      }
    },
    computedToDate: {
      get() {
        return this.$dateFormatGetter(this.search_params.end_date)
      },
      set(val) {
        this.$set(this.search_params, "end_date", this.$dateFormatSetter(val))
      }
    }
  },
  watch: {
    refresh: {
      handler() {
        if (this.refresh) {
          this.list()
        }
      }
    },
    pagination: {
      handler(newVal, oldVal) {
        if (!_.isEqual(newVal, oldVal)) this.refresh = true
      }
    }
  },
  mounted() {
    this.url = this.API_URL + "report/detailed-enrolment/list"
  },
  methods: {
    list(url) {
      this.show_loading = true

      let params = JSON.parse(JSON.stringify(this.search_params))
      let { sortBy, descending, page, itemsPerPage } = this.pagination

      params.start_date = params.start_date ? "gte-" + params.start_date : ""
      params.end_date = params.end_date ? "lte-" + params.end_date : ""
      params.coursetype_ids = params.coursetype_ids ? params.coursetype_ids.join(",") : ""

      let query = this.$queryString(this.pagination, params)

      axios
        .get(this.url + "?" + query)
        .then((res) => {
          this.data = res.data.data
          this.total = res.data.meta.total
          this.refresh = false
          this.show_loading = false

          this.pagination.page = res.data.meta.current_page
          this.pagination.itemsPerPage = Number(res.data.meta.per_page)
        })
        .catch((err) => console.log(err))
    },
    ddlist() {
      let lists = [
        { key: "coursetypes", module: "coursetypes", query: "show_all=true&sort_by=name&sort_order=asc" },
        { key: "courses", module: "courses", query: "show_all=true&sort_by=name&sort_order=asc" }
      ]

      axios.post(this.API_URL + "request/ddlists", lists).then((res) => {
        this.coursetypes = res.data.coursetypes
        this.courses = res.data.courses
      })
    },
    filter(target) {
      this.refresh = true
    },

    reset() {
      this.search_params = {}
      this.refresh = true
    },
    exportExcel() {
      let params = JSON.parse(JSON.stringify(this.search_params))
      params.start_date = params.start_date ? "gte-" + params.start_date : ""
      params.end_date = params.end_date ? "lte-" + params.end_date : ""
      params.coursetype_ids = params.coursetype_ids ? params.coursetype_ids.join(",") : ""

      let query = this.$queryString(this.pagination, params)

      axios({
        url: this.API_URL + "report/detailed-enrolment/export?show_all=true&" + query,
        method: "POST",
        responseType: "arraybuffer"
      }).then((response) => {
        if (this.$arrayBufferValidation(response.data)) {
          const url = window.URL.createObjectURL(new Blob([response.data]))
          const link = document.createElement("a")
          link.href = url
          link.setAttribute("download", "report_detailed_enrolment.xlsx")
          link.click()
        }
      })
    }
  }
}
</script>
